import { Link, NavLink, useLocation } from "react-router-dom";
import { LOGO_URL } from "../lib/constants";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/gaming", label: "Gaming" },
  { to: "/youtube", label: "YouTube Series" },
  { to: "/kids", label: "Kids" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isLanding = pathname === "/";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 ${
        isLanding ? "bg-white/40" : "bg-white/85"
      } backdrop-blur-xl border-b border-[#FF8080]/20`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Arkweaver TFX logo"
            className="h-10 sm:h-12 w-auto drop-shadow-sm transition-transform group-hover:scale-105"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-black text-[15px] text-[#1A1414] tracking-tight">
              ARKWEAVER TFX
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#8B0000] font-bold">
              Guardians of Alkebulan
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 rounded-full font-display font-semibold text-sm tracking-wide transition-all ${
                  isActive
                    ? "text-white bg-[#E60000] shadow-[0_4px_14px_rgba(230,0,0,0.35)]"
                    : "text-[#1A1414] hover:text-[#8B0000] hover:bg-[#FF8080]/15"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 pl-3 border-l border-[#FF8080]/30">
          <div className="flex flex-col leading-tight items-end">
            <span className="text-[9px] uppercase tracking-[0.2em] text-[#1A1414]/60 font-bold">
              Powered by
            </span>
            <span className="text-xs font-display font-bold text-[#8B0000]">
              The Edutainment Box
            </span>
          </div>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#E60000] text-white shadow-arc"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t border-[#FF8080]/20 bg-white"
          >
            <nav className="flex flex-col p-4 gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl font-display font-semibold text-base transition-all ${
                      isActive
                        ? "text-white bg-[#E60000]"
                        : "text-[#1A1414] hover:bg-[#FF8080]/10"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="pt-3 mt-2 border-t border-[#FF8080]/20 px-4 text-xs text-[#1A1414]/60">
                Powered by{" "}
                <span className="text-[#8B0000] font-bold">The Edutainment Box</span>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
